import { ListingResource, OpenHouseField } from '../../system/field';
import { BaseResourceManager } from '../BaseResourceManager';
import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { useReso } from '../../contexts/ResoContext';

export class OpenHouseResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[OpenHouseField.UOHVW_KEY];
  }

  getItemNumber(): number | undefined {
    return this[OpenHouseField.OPEN_HOUSE_ITEM_NUMBER];
  }

  getListingKeyField(): string {
    return OpenHouseField.OPEN_HOUSE_LISTING_KEY;
  }

  getListingKey(): string | undefined {
    return this[OpenHouseField.OPEN_HOUSE_LISTING_KEY];
  }

  // Utility methods for open house resources

  /**
   * Get the open house date
   */
  getDate(): Date | null {
    const dateStr = this[OpenHouseField.OPEN_HOUSE_DATE];
    return dateStr ? new Date(dateStr) : null;
  }

  /**
   * Get the open house start time
   */
  getStartTime(): string | null {
    return this[OpenHouseField.OPEN_HOUSE_START_TIME] || null;
  }

  /**
   * Get the open house end time
   */
  getEndTime(): string | null {
    return this[OpenHouseField.OPEN_HOUSE_END_TIME] || null;
  }

  /**
   * Get the open house type
   */
  getType(): string | null {
    return this[OpenHouseField.OPEN_HOUSE_TYPE] || null;
  }

  /**
   * Check if this is a virtual open house
   */
  isVirtual(): boolean {
    return (
      this[OpenHouseField.OPEN_HOUSE_TYPE]?.toLowerCase().includes('virtual') ||
      this[OpenHouseField.OPEN_HOUSE_METHOD]
        ?.toLowerCase()
        .includes('virtual') ||
      !!this[OpenHouseField.VIRTUAL_OPEN_HOUSE_URL]
    );
  }

  /**
   * Get the virtual open house URL if available
   */
  getVirtualUrl(): string | null {
    return this[OpenHouseField.VIRTUAL_OPEN_HOUSE_URL] || null;
  }

  /**
   * Get the attended by information
   */
  getAttendedBy(): string | null {
    return this[OpenHouseField.OPEN_HOUSE_ATTENDED_BY] || null;
  }

  /**
   * Get the remarks
   */
  getRemarks(): string | null {
    return this[OpenHouseField.OPEN_HOUSE_REMARKS] || null;
  }
}

export class OpenHouseResourceManager extends BaseResourceManager<OpenHouseResource> {
  constructor(
    resourceType: ListingResource,
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: {
      enableCwa?: boolean;
      useTranslation?: boolean;
    } = {},
  ) {
    super(resourceType, executeQuery, fieldTranslator, taskManager, options);
  }

  /**
   * Core mapping function from API data to OpenHouseResource
   */
  protected mapFromApiCore(apiData: any): OpenHouseResource {
    return new OpenHouseResource(apiData);
  }

  /**
   * Core mapping function from OpenHouseResource to API data
   */
  protected mapToApiCore(resource: OpenHouseResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Get upcoming open houses
   */
  public async getUpcoming(listingKey?: string): Promise<OpenHouseResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getUpcoming' },
      async (_taskId) => {
        try {
          // Get all open houses
          const allOpenHouses = await this.list(listingKey);

          // Filter for only future open houses
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          return allOpenHouses
            .filter((openHouse) => {
              const openHouseDate = openHouse.getDate();
              return openHouseDate && openHouseDate >= today;
            })
            .sort((a, b) => {
              const dateA = a.getDate() || new Date();
              const dateB = b.getDate() || new Date();
              return dateA.getTime() - dateB.getTime();
            });
        } catch (error) {
          console.error('Error getting upcoming open houses:', error);
          return [];
        }
      },
    );
  }

  /**
   * Get past open houses
   */
  public async getPast(listingKey?: string): Promise<OpenHouseResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getPast' },
      async (_taskId) => {
        try {
          // Get all open houses
          const allOpenHouses = await this.list(listingKey);

          // Filter for only past open houses
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          return allOpenHouses
            .filter((openHouse) => {
              const openHouseDate = openHouse.getDate();
              return openHouseDate && openHouseDate < today;
            })
            .sort((a, b) => {
              const dateA = a.getDate() || new Date();
              const dateB = b.getDate() || new Date();
              return dateB.getTime() - dateA.getTime(); // Sort newest first
            });
        } catch (error) {
          console.error('Error getting past open houses:', error);
          return [];
        }
      },
    );
  }

  /**
   * Get virtual open houses
   */
  public async getVirtual(listingKey?: string): Promise<OpenHouseResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getVirtual' },
      async (_taskId) => {
        try {
          // Get all open houses
          const allOpenHouses = await this.list(listingKey);

          // Filter for only virtual open houses
          return allOpenHouses.filter((openHouse) => openHouse.isVirtual());
        } catch (error) {
          console.error('Error getting virtual open houses:', error);
          return [];
        }
      },
    );
  }
}

/**
 * Create a new OpenHouseResource with required fields
 */
export function createOpenHouseResource(
  data: Partial<Record<string, any>> = {},
): OpenHouseResource {
  const defaults = {
    [OpenHouseField.OPEN_HOUSE_ITEM_NUMBER]: 0,
  };

  return new OpenHouseResource({
    ...defaults,
    ...data,
  });
}
